import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { Router, RouterLink } from '@angular/router';
import { AccessProfile } from '@app/component/page/company/access-profile/models/access-profile';
import { Company, COMPANY_STATE } from '@app/interface/company';
import { MkgUser } from '@app/interface/user';
import { DataService } from '@app/service/data.service';
import { LayoutService } from '@app/service/layout.service';
import { BOOLEAN_TWO } from '@app/shared/common-enums/boolean-two';
import { RO_STATES } from '@app/shared/lists/ro-states';
import { roles } from '@app/shared/lists/roles';
import { CanAccessPipe } from '@app/shared/pipes/can-access.pipe';
import { MenuSelectedPipe } from '@app/shared/pipes/menu-selected.pipe';
import { environment } from '@env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { LegacyService } from '@app/component/page/company/legacy-os/legacy.service';
import { CachedOsService } from '@app/component/page/company/os/services/cached-os.service';
import { DataCacheService } from '@app/service/data-cache.service';
import { ReverseRouterService } from '@app/service/reverse-router.service';
import { OLD_MEKANIC_MENU, OldSideMenu } from '../header/menu/old-mekanic-menu';
import { OLD_TCAR_INTEGRATED_MENU } from '../header/menu/old-tcar-integrated-menu';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'mkg-old-aside-menu',
  standalone: true,
  imports: [
    MatListModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    TranslateModule,
    CanAccessPipe,
    RouterLink,
    MenuSelectedPipe
  ],
  templateUrl: './old-aside-menu.component.html',
  styleUrl: './old-aside-menu.component.scss',
  encapsulation: ViewEncapsulation.Emulated
})
export class OldAsideMenuComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  private _commonOsService = inject(CachedOsService);
  private _dataCacheService = inject(DataCacheService);
  private _reverseRouterService = inject(ReverseRouterService);
  private _legacyService = inject(LegacyService);
  private _dataService = inject(DataService);

  public layout = inject(LayoutService);
  public router = inject(Router);

  protected roStatesKeys: string[] = [];
  
  menu: OldSideMenu[] = [];
  isProduction = environment.production;
  isTecincoMember: boolean;

  accessProfile: AccessProfile;
  userRole: number;
  
  ngOnInit(): void {
    this._dataService.ready$.pipe(takeUntil(this._destroy$)).subscribe(([user, company]) => {
      if (company.currentState === COMPANY_STATE.blocked) {
        return
      }

      this.setMenu(company, user);
      this.isTecincoMember = user.isTecincoMember;
      this.roStatesKeys = Object.keys(RO_STATES);
    });
    
    this._dataService.accessProfile$.pipe(takeUntil(this._destroy$)).subscribe(profile => {
      this.accessProfile = profile;
    })

    this._dataService.userRole$.pipe(takeUntil(this._destroy$)).subscribe(role => {
      this.userRole = role;
    })
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private setMenu(company: Company, user: MkgUser) {
    const isTCarIntegrated = company.currentState === COMPANY_STATE.integrated;

    if (isTCarIntegrated) {
      this.menu = OLD_TCAR_INTEGRATED_MENU(company);
    } else {
      this.menu = OLD_MEKANIC_MENU(company, user);
      this._legacyService.getAll().then(legacies => {
        if (Array.isArray(legacies) && legacies.length) {
          const configMenu = this.menu.pop();
          this.menu.push({
            component: 'legacy',
            blockedRoles: [roles.mechanic.id],
            icon: 'join_left',
            href: ['company', company.id, 'legacy'],
            profileModule: "LEGACY",
          }, configMenu);
        }

        if (company.discountControl !== BOOLEAN_TWO.TRUE) {
          let discountControlIndex = this.menu.findIndex(menu => menu.component === "discount-control");
          if (discountControlIndex) {
            this.menu.splice(discountControlIndex, 1);
          };
        };

        if (!company.tokenWhats) {
          let recordsIndex = this.menu.findIndex(menu => menu.component === "records");
          if (recordsIndex) {
            const controlMessagesIndex = this.menu[recordsIndex].childs.findIndex(submenu => submenu.component === "control-messages");
            if (controlMessagesIndex) {
              this.menu[recordsIndex].childs.splice(controlMessagesIndex, 1);
            }
          };
        }
      });
    }
  }

  public navigateOrExpand(menu: OldSideMenu, isTopLevel: boolean) {
    if (!menu.href || this.layout.component === menu.component || menu.component === 'budgets') {
      this.layout.toggle(menu.component, isTopLevel);
      return
    }
    this.router.navigate(menu.href, {
      queryParams: menu.queryParams
    });
  }
  
  public resetCache(component: string) {
    this._reverseRouterService.clear();
    this._dataCacheService.reset();

    if (!["budgets", "opened", "rejected", "budget", "closed-budget", "ended", "ro", "closed"].includes(component)) {
      this._commonOsService.clearCache();
    }
  }
}
